import { useQuery } from '@tanstack/react-query';
import { Navigate, useRouterState } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { FormAlert } from '@/components/ui/form-alert';
import { genericErrorMessage, httpStatus } from '@/lib/api-errors';
import { authApi } from '@/lib/auth-api';
import { CHANGE_PASSWORD_PATH } from '@/routes/change-password';
import { useAuthStore } from '@/stores/auth';

export const WHOAMI_KEY = ['platform', 'whoami'] as const;

/**
 * Garde des routes protégées : sans jeton → /login ; jeton présent → /whoami (l'API relit l'admin en base,
 * un compte suspendu ou un jeton expiré répondent 401) ; 401 → session nettoyée puis /login. Tant que /whoami
 * renvoie mustChangePassword, seule la page de changement de mot de passe est accessible (côté client seulement,
 * TD-033).
 */
export const RequireAuth = ({ children }: { children: ReactNode }) => {
  const accessToken = useAuthStore((s) => s.accessToken);
  const setAdmin = useAuthStore((s) => s.setAdmin);
  const sessionExpired = useAuthStore((s) => s.sessionExpired);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const whoami = useQuery({
    queryKey: [...WHOAMI_KEY, accessToken],
    queryFn: authApi.whoami,
    enabled: Boolean(accessToken),
    staleTime: 60_000,
  });
  const unauthorized = httpStatus(whoami.error) === 401;

  useEffect(() => {
    if (whoami.data) setAdmin(whoami.data);
  }, [whoami.data, setAdmin]);

  // Filet si le 401 n'est pas passé par l'intercepteur du client API. Session nettoyée AVANT la redirection :
  // /login renvoie vers / tant qu'un jeton est présent.
  useEffect(() => {
    if (unauthorized) clearAuth({ expired: true });
  }, [unauthorized, clearAuth]);

  if (!accessToken) return <Navigate to="/login" search={sessionExpired ? { reason: 'expired' } : {}} replace />;

  if (whoami.isPending || unauthorized) {
    return (
      <div className="flex flex-1 items-center justify-center p-6 text-muted-foreground" role="status">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden />
        Vérification de la session…
      </div>
    );
  }

  if (whoami.isError) {
    return (
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center gap-3 p-6">
        <FormAlert>Impossible de vérifier la session : {genericErrorMessage(whoami.error).toLowerCase()}</FormAlert>
        <Button variant="outline" onClick={() => void whoami.refetch()}>
          Réessayer
        </Button>
      </div>
    );
  }

  if (whoami.data.mustChangePassword && pathname !== CHANGE_PASSWORD_PATH) return <Navigate to={CHANGE_PASSWORD_PATH} replace />;

  return <>{children}</>;
};
